import { useMemo, useState } from 'react'
import { Check, ChevronDown, Package, X } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { ReportFilters } from '@/features/reports/lib/report-filters'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useSalesReportQuery } from '@/features/reports/api/use-sales-report-query'
import { formatCurrency, formatNumber } from '@/features/reports/lib/format'
import { cn } from '@/lib/utils'

/**
 * How many articles the picker offers before the search is the only way through. Production holds
 * roughly 5.000 articles of which about a hundred are in use, so this is generous for the list it
 * actually renders and small enough that the request stays one page.
 */
const CANDIDATE_LIMIT = 300

interface ArticleFilterProps {
  /** The current slice — the candidates are what sold *in it*, so the list follows the period. */
  filters: ReportFilters
  selected: Array<string>
  onChange: (skus: Array<string>) => void
}

/**
 * Pick individual articles to report on (CHG-2026-09-article-location-filter, D1).
 *
 * **Sorted by revenue in the chosen period, not alphabetically.** Whoever reports on an article
 * almost always means one that sells; alphabetically the top of the list would be whatever the
 * webbill import happened to name with an A.
 *
 * **It offers what sold in the period.** An article that sold nothing has no row here — it is not
 * missing, it is the subject of the zero-seller tab, which answers "warum verkauft sich der nie?"
 * properly instead of hiding it three thousand rows down a picker. The one exception is an article
 * that is already picked: it stays listed whatever it sold, or there would be no way to unpick it
 * after changing the period.
 *
 * The candidate list deliberately ignores the article filter itself. Feeding the filter back into
 * the list that produces it would leave the reader with exactly the three articles they had already
 * chosen and no way to add a fourth.
 */
export function ArticleFilter({
  filters,
  selected,
  onChange,
}: ArticleFilterProps) {
  const { t } = useTranslation('reports')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const candidates = useSalesReportQuery(
    {
      ...filters,
      skus: [],
      dimension: 'article',
      measure: 'revenue',
      limit: CANDIDATE_LIMIT,
    },
    open || selected.length > 0,
  )

  const options = useMemo(() => {
    const rows = candidates.data?.rows ?? []
    const known = new Set(rows.map((r) => r.key))
    const out = rows.map((r) => ({
      sku: r.key,
      name: r.label ?? r.key,
      revenueCents: r.revenueCents,
      units: r.units,
      sold: true,
    }))
    // A picked article that sold nothing in the current period keeps its row, unpickable otherwise.
    for (const sku of selected) {
      if (!known.has(sku)) {
        out.push({
          sku,
          name: sku,
          revenueCents: 0,
          units: 0,
          sold: false,
        })
      }
    }
    return out
  }, [candidates.data, selected])

  const needle = query.trim().toLowerCase()
  const visible = needle
    ? options.filter(
        (o) =>
          o.name.toLowerCase().includes(needle) ||
          o.sku.toLowerCase().includes(needle),
      )
    : options

  function toggle(sku: string) {
    onChange(
      selected.includes(sku)
        ? selected.filter((s) => s !== sku)
        : [...selected, sku],
    )
  }

  const label =
    selected.length === 1
      ? (options.find((o) => o.sku === selected[0])?.name ?? selected[0])
      : selected.length > 1
        ? t('articleFilter.count', { value: selected.length })
        : t('articleFilter.label')

  return (
    <div className="flex w-full items-center gap-1 lg:w-auto">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'h-12 min-w-0 flex-1 justify-start gap-2 font-normal sm:h-9 lg:flex-none',
              selected.length > 0 && 'border-foreground bg-muted',
            )}
          >
            <Package
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="truncate">{label}</span>
            <ChevronDown
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[min(22rem,calc(100vw-2rem))] p-0"
        >
          <div className="border-b border-border p-2">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('articleFilter.searchPlaceholder')}
              aria-label={t('articleFilter.searchPlaceholder')}
              className="h-9"
            />
          </div>

          <div className="max-h-[min(18rem,50vh)] overflow-y-auto p-1">
            {candidates.isLoading ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('articleFilter.loading')}
              </p>
            ) : visible.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('articleFilter.noMatches')}
              </p>
            ) : (
              visible.map((option) => {
                const checked = selected.includes(option.sku)
                return (
                  <button
                    key={option.sku}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={checked}
                    onClick={() => toggle(option.sku)}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-muted"
                  >
                    <Check
                      className={cn(
                        'h-4 w-4 shrink-0',
                        !checked && 'invisible',
                      )}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {option.sku}
                      </span>
                    </span>
                    <span className="shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                      {option.sold ? (
                        <>
                          <span className="block">
                            {formatCurrency(option.revenueCents)}
                          </span>
                          <span className="block">
                            {t('articleFilter.units', {
                              value: formatNumber(option.units),
                            })}
                          </span>
                        </>
                      ) : (
                        t('articleFilter.notSoldInPeriod')
                      )}
                    </span>
                  </button>
                )
              })
            )}
          </div>

          <p className="border-t border-border px-3 py-2 text-xs leading-4 text-muted-foreground">
            {t('articleFilter.sortHint')}
          </p>
        </PopoverContent>
      </Popover>

      {selected.length > 0 ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 w-9 shrink-0 p-0"
          onClick={() => onChange([])}
          aria-label={t('articleFilter.clear')}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  )
}
